// theme/presets/deepSignal.ts — "Deep Signal" backdrop colour derivation.
//
// Derives the backdrop's lobe colours from the ACTIVE accent (purple / teal /
// custom) in OKLCH: keep the hue, halve the chroma, drop lightness to a dim
// 0.26 core — hard-clamped at L ≤ 0.30 so the brightest background pixel can
// never compete with card surfaces or break glass-mode text contrast (the
// worst-case composite is asserted in deepSignal.test.ts).
//
// OKLab math per Björn Ottosson (https://bottosson.github.io/posts/oklab/).

import { hexToRgb } from './color';

/** Hard ceiling on backdrop lightness (glass-mode legibility guardrail).
 *  0.35 keeps worst-case glass text comfortably past 4.5:1 (asserted in tests)
 *  while making the signal clearly visible rather than subliminal. */
export const LOBE_MAX_L = 0.35;

// ── sRGB ↔ OKLCH ────────────────────────────────────────────────────────────

function srgbToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  const v = c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, v)) * 255);
}

export interface Oklch {
  L: number;
  C: number;
  /** Hue in degrees [0, 360). */
  H: number;
}

export function hexToOklch(hex: string): Oklch {
  const { r, g, b } = hexToRgb(hex);
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const C = Math.sqrt(a * a + bb * bb);
  let H = (Math.atan2(bb, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { L, C, H };
}

/** OKLCH → {r,g,b} 0–255, or null when out of the sRGB gamut. */
function oklchToRgbExact(L: number, C: number, H: number): { r: number; g: number; b: number } | null {
  const hr = (H * Math.PI) / 180;
  const a = C * Math.cos(hr);
  const bb = C * Math.sin(hr);

  const l_ = L + 0.3963377774 * a + 0.2158037573 * bb;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * bb;
  const s_ = L - 0.0894841775 * a - 1.291485548 * bb;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  const eps = 1e-4;
  if (lr < -eps || lr > 1 + eps || lg < -eps || lg > 1 + eps || lb < -eps || lb > 1 + eps) return null;
  return { r: linearToSrgb(lr), g: linearToSrgb(lg), b: linearToSrgb(lb) };
}

/** OKLCH → {r,g,b} 0–255, gamut-mapped by walking chroma toward 0. */
export function oklchToRgb(L: number, C: number, H: number): { r: number; g: number; b: number } {
  let rgb = oklchToRgbExact(L, C, H);
  let c = C;
  while (rgb === null && c > 0) {
    c = Math.max(0, c - 0.004);
    rgb = oklchToRgbExact(L, c, H);
  }
  return rgb ?? { r: 0, g: 0, b: 0 };
}

/** OKLCH → rgba() string, gamut-mapped by walking chroma toward 0. */
export function oklchToRgba(L: number, C: number, H: number, alpha: number): string {
  const { r, g, b } = oklchToRgb(L, C, H);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Deep-Signal palette ─────────────────────────────────────────────────────

export interface DeepSignalColors {
  /** Primary lobe hottest stop (L clamped at LOBE_MAX_L). */
  lobeCore: string;
  /** Primary lobe mid falloff stop. */
  lobeMid: string;
  /** Counterweight lobe (hue-adjacent, dimmer) core + mid. */
  counterCore: string;
  counterMid: string;
}

/**
 * Derive the backdrop lobes from the active accent. Dark mode: dim OKLCH lobes
 * (chroma halved, L 0.26 core / 0.17 mid, ceiling 0.30). Light mode: the same
 * geometry as a faint low-alpha wash of the accent over the paper tone.
 */
export function deriveDeepSignal(accentHex: string, mode: 'dark' | 'light'): DeepSignalColors {
  const { C, H } = hexToOklch(accentHex);
  const counterH = (H - 30 + 360) % 360;

  if (mode === 'light') {
    const { r, g, b } = hexToRgb(accentHex);
    return {
      lobeCore: `rgba(${r}, ${g}, ${b}, 0.05)`,
      lobeMid: `rgba(${r}, ${g}, ${b}, 0.03)`,
      counterCore: oklchToRgba(Math.min(0.45, LOBE_MAX_L + 0.15), Math.min(C * 0.4, 0.07), counterH, 0.03),
      counterMid: oklchToRgba(Math.min(0.45, LOBE_MAX_L + 0.15), Math.min(C * 0.25, 0.04), counterH, 0.02),
    };
  }

  return {
    lobeCore: oklchToRgba(Math.min(0.32, LOBE_MAX_L), Math.min(C * 0.55, 0.11), H, 0.95),
    lobeMid: oklchToRgba(0.2, Math.min(C * 0.3, 0.055), H, 0.55),
    counterCore: oklchToRgba(Math.min(0.26, LOBE_MAX_L), Math.min(C * 0.45, 0.085), counterH, 0.4),
    counterMid: oklchToRgba(0.17, Math.min(C * 0.22, 0.04), counterH, 0.22),
  };
}

/** The absolute brightest colour the dark backdrop can emit (for contrast tests). */
export function brightestBackdropHex(accentHex: string): string {
  const { C, H } = hexToOklch(accentHex);
  const { r, g, b } = oklchToRgb(LOBE_MAX_L, Math.min(C * 0.55, 0.11), H);
  const to2 = (n: number) => n.toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

// ── GPU-tier (slice B) numeric derivation ──────────────────────────────────

// ── "Register" material ─────────────────────────────────────────
// Replaces the "Sundown Signal" sun-and-neon-grid (retired in 1.25.00: a banded
// retro sun over a magenta perspective grid is a fully-formed generated-art
// genre, and it carried no product meaning — nothing about juries or a public
// record is expressed by a sunset).
//
// The backdrop is now a MATERIAL rather than a picture: paper stock, one long
// linear ramp of light across it, and ruling that shows only in the margins.
// Nothing is ever drawn behind the reading column, which is why the old
// hand-authored ±400px blackout scrim could be deleted outright instead of
// re-tuned. Everything stays accent-derived, so the seven accent presets and a
// custom hex still retint the whole field exactly as they did before.

export interface RegisterColors {
  /** Ramp start — the lit corner. Accent-hued, L hard-capped by LOBE_MAX_L. */
  tideFrom: string;
  /** Ramp end — the shaded corner. */
  tideTo: string;
  /** Gutter ruling: a neutral hairline, never accent-tinted. */
  rule: string;
}

/**
 * Derive the Register field from the active accent.
 *
 * Dark: two dim accent-hued stops (chroma cut hard, lightness clamped under
 * LOBE_MAX_L) painted at low alpha over bgRoot, so the brightest pixel the
 * backdrop can emit still clears the glass-mode legibility guardrail asserted
 * in deepSignal.test.ts. Light: the same geometry as a faint wash of the accent
 * over the paper tone.
 *
 * There is deliberately no radial anything. A radial accent bloom behind the
 * feed is the "decorative spotlight glow" tell; a linear ramp reads as a room
 * lit from one side, which is a material property rather than an effect.
 */
export function deriveRegister(accentHex: string, mode: 'dark' | 'light'): RegisterColors {
  const { C, H } = hexToOklch(accentHex);

  if (mode === 'light') {
    const { r, g, b } = hexToRgb(accentHex);
    return {
      tideFrom: `rgba(${r}, ${g}, ${b}, 0.05)`,
      tideTo: `rgba(${r}, ${g}, ${b}, 0.02)`,
      rule: 'rgba(20, 18, 22, 0.06)',
    };
  }

  return {
    tideFrom: oklchToRgba(Math.min(0.30, LOBE_MAX_L), Math.min(C * 0.35, 0.05), H, 0.55),
    tideTo: oklchToRgba(0.17, Math.min(C * 0.2, 0.03), H, 0.30),
    rule: 'rgba(255, 255, 255, 0.05)',
  };
}

/** RGB triplet normalised to 0–1 for shader uniforms. */
export type Vec3 = [number, number, number];

export function hexToVec3(hex: string): Vec3 {
  const { r, g, b } = hexToRgb(hex);
  return [r / 255, g / 255, b / 255];
}

/**
 * The high tier's shader uniforms — same OKLCH derivation as the CSS lobes
 * (same hue, halved chroma, L ceiling), as normalised vec3s.
 */
export function deriveDeepSignalGl(accentHex: string): { core: Vec3; counter: Vec3 } {
  const { C, H } = hexToOklch(accentHex);
  const counterH = (H - 30 + 360) % 360;
  const toVec = (L: number, c: number, h: number): Vec3 => {
    const { r, g, b } = oklchToRgb(L, c, h);
    return [r / 255, g / 255, b / 255];
  };
  return {
    core: toVec(Math.min(0.32, LOBE_MAX_L), Math.min(C * 0.55, 0.11), H),
    counter: toVec(Math.min(0.26, LOBE_MAX_L), Math.min(C * 0.45, 0.085), counterH),
  };
}
